import { Check, X, Crown, Zap, Building2 } from "lucide-react";
import EarlyAccessCta from "./EarlyAccessCta";
import DemoModal from "./DemoModal";
import CurrencyToggle from "./CurrencyToggle";
import { useCurrency, fmtPrice } from "@/lib/currency";

// Prices are stored in ZAR (cents-equivalent integer rand). USD values are
// the specific display prices set by the platform owner — not a raw FX conversion —
// so we keep both columns explicitly instead of dividing by the rate on the fly.
const plans = [
    {
        id: "starter",
        name: "Starter",
        tagline: "For solo reps testing the waters",
        price: { ZAR: "R0", USD: "$0" },
        suffix: "/60-day trial",
        note: "No credit card required",
        icon: Zap,
        ctaPlan: null,
        ctaLabel: "Start Free Trial",
        highlighted: false,
        features: [
            { ok: true, text: "1 Sales Rep seat" },
            { ok: true, text: "1 Admin seat (required)" },
            { ok: true, text: "1 Manager seat (required)" },
            { ok: true, text: "5 calls per rep / month" },
            { ok: true, text: "Meeting Bot (20 min cap)" },
            { ok: true, text: "1 manual override / meeting" },
            { ok: true, text: "Google SSO" },
            { ok: false, text: "PDF Export" },
            { ok: false, text: "Team Analytics" },
            { ok: false, text: "Custom Branding" },
        ],
    },
    {
        id: "pro",
        name: "Pro",
        tagline: "Small teams that close on discovery",
        price: { ZAR: "R1,299", USD: "$72" },
        suffix: "/month",
        note: "Month-to-month · cancel anytime",
        icon: Crown,
        ctaPlan: "pro",
        ctaLabel: "Get Started with Pro",
        highlighted: true,
        features: [
            { ok: true, text: "3 Sales Rep seats" },
            { ok: true, text: "1 Admin seat (required)" },
            { ok: true, text: "1 Manager seat (required)" },
            { ok: true, text: "12 calls per rep / month" },
            { ok: true, text: "Unlimited Meeting Bot" },
            { ok: true, text: "3 manual overrides / meeting" },
            { ok: true, text: "PDF Export" },
            { ok: true, text: "Basic Team Analytics" },
            { ok: true, text: "Google SSO" },
            { ok: true, extraRep: { ZAR: "+R299/mo per extra rep", USD: "+$17/mo per extra rep" }, extraMgr: { ZAR: "+R179/mo extra admin/mgr", USD: "+$10/mo extra admin/mgr" } },
            { ok: true, text: "Optional Training & Setup — R30,000 / $1,667" },
            { ok: false, text: "Custom Branding" },
        ],
    },
    {
        id: "business",
        name: "Business",
        tagline: "For full sales orgs that coach at scale",
        price: { ZAR: "R4,499", USD: "$250" },
        suffix: "/month",
        note: "Month-to-month · cancel anytime",
        icon: Building2,
        ctaPlan: "business",
        ctaLabel: "Get Started with Business",
        highlighted: false,
        features: [
            { ok: true, text: "10 Sales Rep seats" },
            { ok: true, text: "1 Admin seat (required)" },
            { ok: true, text: "Unlimited Manager seats" },
            { ok: true, text: "15 calls per rep / month" },
            { ok: true, text: "Unlimited Meeting Bot" },
            { ok: true, text: "5 manual overrides / meeting" },
            { ok: true, text: "PDF Export" },
            { ok: true, text: "Full Team Analytics + Leaderboard" },
            { ok: true, text: "Custom Branding" },
            { ok: true, extraRep: { ZAR: "+R349/mo per extra rep", USD: "+$19/mo per extra rep" }, extraMgr: { ZAR: "+R149/mo extra admin/mgr", USD: "+$8/mo extra admin/mgr" } },
            { ok: true, text: "Optional Training & Setup — R55,000 / $3,056" },
            { ok: true, text: "Priority support" },
        ],
    },
];

// Top-up packs & add-ons — shown below the main plan cards.
const ADDONS = [
    { id: "pack-25",  name: "Call Pack — 25 calls",      price: { ZAR: "R599",  USD: "$33" }, suffix: "one-time", sub: "Top up any Pro or Business plan mid-month" },
    { id: "pack-50",  name: "Call Pack — 50 calls",      price: { ZAR: "R999",  USD: "$56" }, suffix: "one-time", sub: "Better value for heavier-use weeks" },
    { id: "power",    name: "Power Caller",              price: { ZAR: "R399",  USD: "$22" }, suffix: "one-time", sub: "+15 calls / rep — single rep boost" },
];

export default function PricingTable({ compact = false }) {
    const { currency, fxRate } = useCurrency();
    const rateLabel = `R${fxRate} = $1`;

    return (
        <div className="w-full">
            <div className="flex flex-col items-center gap-5">
                <CurrencyToggle data-testid="pricing-currency-toggle" />
                <p className="text-sm text-zinc-500 text-center max-w-md">
                    {currency === "USD"
                        ? `Paystack processes payments in ZAR. USD figures are for reference — billed at ${rateLabel}.`
                        : "Prices in South African Rand, exclusive of VAT. Paid via Paystack."}
                </p>
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {plans.map((p) => {
                    const Icon = p.icon;
                    const isHl = p.highlighted;
                    return (
                        <div
                            key={p.id}
                            data-testid={`pricing-card-${p.id}`}
                            className={`relative rounded-2xl border p-7 flex flex-col bg-white text-zinc-900 ${
                                isHl
                                    ? "border-brand-signature ring-1 ring-brand-signature/40 shadow-[0_24px_60px_-15px_rgba(244,185,66,0.35)]"
                                    : "border-zinc-200"
                            }`}
                        >
                            {isHl && (
                                <span className="absolute -top-3 left-7 inline-flex items-center rounded-full bg-brand-signature px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-signature-strong shadow-lg">
                                    Most Popular
                                </span>
                            )}

                            <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-5 ${isHl ? "bg-brand-tint text-brand-primary" : "bg-zinc-100 text-zinc-700"}`}>
                                <Icon className="h-5 w-5" />
                            </div>

                            <h3 className="font-display text-2xl font-bold tracking-tight text-zinc-900">{p.name}</h3>
                            <p className="mt-1 text-sm text-zinc-500">{p.tagline}</p>

                            <div className="mt-6 flex items-baseline gap-1.5">
                                <span data-testid={`pricing-price-${p.id}`} className="font-display text-5xl font-bold tracking-tight">
                                    {p.price[currency]}
                                </span>
                                <span className="text-sm text-zinc-500">{p.suffix}</span>
                            </div>
                            {p.note && (
                                <p className="mt-2 text-xs text-zinc-500 font-medium">{p.note}</p>
                            )}

                            <EarlyAccessCta
                                data-testid={`pricing-cta-${p.id}`}
                                plan={p.ctaPlan}
                                label={p.ctaLabel}
                                className={`mt-6 w-full h-11 font-medium ${
                                    isHl ? "bg-brand-primary hover:bg-brand-hover text-white" : "bg-zinc-900 hover:bg-zinc-800 text-white"
                                }`}
                            />

                            {!compact && (
                                <ul className="mt-7 space-y-3">
                                    {p.features.map((f, i) => {
                                        // Composite extra-seat rows (render both seat types on one line
                                        // so the card height stays compact and prices stay side-by-side).
                                        if (f.extraRep) {
                                            return (
                                                <li key={i} className="flex items-start gap-2.5 text-sm">
                                                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-brand-primary" />
                                                    <span className="text-zinc-700">
                                                        {f.extraRep[currency]} · {f.extraMgr[currency]}
                                                    </span>
                                                </li>
                                            );
                                        }
                                        return (
                                            <li key={i} className="flex items-start gap-2.5 text-sm">
                                                {f.ok ? (
                                                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-brand-primary" />
                                                ) : (
                                                    <X className="h-4 w-4 mt-0.5 shrink-0 text-zinc-300" />
                                                )}
                                                <span className={!f.ok ? "text-zinc-400 line-through" : "text-zinc-700"}>
                                                    {f.text}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    );
                })}
            </div>

            {!compact && (
                <div className="mt-14">
                    <div className="max-w-2xl mx-auto text-center">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Top-ups &amp; add-ons</p>
                        <h3 className="mt-3 font-display text-3xl font-bold tracking-tight text-zinc-900">
                            Need more calls this month? Grab a pack.
                        </h3>
                        <p className="mt-3 text-sm text-zinc-600">
                            One-time purchases that drop into your plan instantly. No commitment, no auto-renew.
                        </p>
                    </div>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {ADDONS.map((a) => (
                            <div key={a.id} data-testid={`addon-card-${a.id}`} className="rounded-xl border border-zinc-200 bg-white p-6 hover:border-brand-accent-muted hover:shadow-md transition-all">
                                <p className="text-sm font-semibold text-zinc-900">{a.name}</p>
                                <p className="mt-1 text-xs text-zinc-500">{a.sub}</p>
                                <div className="mt-4 flex items-baseline gap-1.5">
                                    <span className="font-display text-2xl font-bold tracking-tight text-zinc-900">{a.price[currency]}</span>
                                    <span className="text-xs text-zinc-500">{a.suffix}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-12 text-center">
                <p className="text-sm text-zinc-600">
                    Need something custom? <DemoModal trigger={<button data-testid="pricing-talk-sales" className="text-brand-primary hover:text-brand-hover font-medium underline underline-offset-4">Talk to sales</button>} />
                </p>
            </div>
        </div>
    );
}

// Re-export the price formatter for any component that needs it (e.g. the ROI calculator).
export { fmtPrice };
